import './index.scss'

import React from 'react'
import T from 'prop-types'
import BEMHelper from 'react-bem-helper'
import { values, groupBy, sortBy, max, get } from 'lodash'
import { getHours, format } from 'date-fns'
import { formatDate } from 'lib/date'
import { uppercaseFirst } from 'lib/utils'
import { Row } from 'ui'

const bem = new BEMHelper('time-table')

export default function TimeTable({
  data,
  selected,
  onSelect,
  admin,
  horizontal,
  showNumberOfRegistrations = true,
}) {
  // Group slots by hour
  const grouped = data
    .map((day) => ({
      ...day,
      slots: sortBy(
        values(
          groupBy(sortBy(day.slots || [], 'start_time'), ({ start_time }) =>
            start_time ? getHours(start_time) : null,
          ),
        ),
        '[0].start_time',
      ).filter((hour) => {
        // If all slots have ended, don't show the hour
        const slotsNotEnded = hour.filter(({ closed }) => !closed)
        return admin || slotsNotEnded.length > 0
      }),
    }))
    .filter(({ slots }) => slots.length > 0)

  // Find out how many slots we have in the hour with the most slots
  const columns = max(grouped.map(({ slots }) => max(slots.map((slot) => slot.length))))
  const columnArray = Array.from(Array(columns).keys()).map((i) => i)

  const getSelectHandler = (slot) => () => onSelect(slot)

  const count = Math.max(2, columns)

  return (
    <ul
      {...bem('', { admin, horizontal, tight: count > 4, 'super-tight': count > 5 })}
      style={{ '--columns': count }}
    >
      {grouped.map(({ date: dateString, slots, options }) => {
        const date = new Date(dateString)

        return (
          <li key={format(date, 'yyyy-MM-dd')} {...bem('day')}>
            <h2 {...bem('day-name')}>
              {uppercaseFirst(formatDate(date, 'EEEE d. MMMM yyyy'))}
              {(options || !options) && <span {...bem('options')}>{options}</span>}
            </h2>

            <div {...bem('content')}>
              {(!slots || slots.length === 0) && <p {...bem('empty')}>Ingen tilgjengelig</p>}

              {slots.map((list, index) => (
                <nav key={index} {...bem('slots')}>
                  {columnArray.map((index) => {
                    const slot = list[index] || {
                      placeholder: true,
                      id: index,
                    }

                    const { id, start_time, placeholder, closed: resClosed } = slot
                    const closed = placeholder || (admin ? false : resClosed)
                    const available = Number(get(slot, 'capacity.available', 0))
                    const total = Number(get(slot, 'capacity.total', 0))
                    const percentage = total ? ((total - available) * 100) / total : null
                    const full = get(slot, 'capacity.full')
                    const showProgress = percentage !== null && admin && !closed

                    return (
                      <button
                        key={id}
                        type="button"
                        {...bem('slot', {
                          closed,
                          full: full && !closed,
                          'closed-admin': admin && resClosed,
                          admin,
                          percentage,
                          selected: `${selected}` === `${id}`,
                        })}
                        disabled={closed || (admin ? false : full)}
                        onClick={getSelectHandler(slot)}
                      >
                        {closed ? (
                          <span {...bem('closed')} />
                        ) : (
                          <Row center style={{ justifyContent: 'center' }}>
                            {format(start_time, 'HH:mm')}
                            {showNumberOfRegistrations && (
                              <span {...bem('registrations')}>
                                {total - available}
                                <span style={{ opacity: 0.5, margin: '0 1px' }}>/</span>
                                {total}
                              </span>
                            )}
                          </Row>
                        )}

                        {showProgress && (
                          <span
                            {...bem('percentage')}
                            style={{ '--percentage': `${percentage}%` }}
                          />
                        )}
                      </button>
                    )
                  })}
                </nav>
              ))}
            </div>
          </li>
        )
      })}
    </ul>
  )
}

TimeTable.propTypes = {
  data: T.array.isRequired,
  onSelect: T.func.isRequired,
  admin: T.bool,
  selected: T.oneOfType([T.string, T.number]),
  horizontal: T.bool,
}
